import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ibsys/kit';
import { PackageIcon } from 'lucide-react';
import { FC, useCallback, useMemo, useState } from 'react';
import type { BlockResponse } from '@/entities/expert-report';
import { RuleCheckboxItem } from './RuleCheckboxItem';
import { blocksHaveMatchingRules, ruleMatchesSearch } from './ruleSearch';
import type { RuleWithPath } from './types';

type TreeBlockNodeProps = {
  block: BlockResponse;
  reportId: number;
  reportName: string;
  depth: number;
  blockPath: string[];
  search: string;
  linkedSet: Set<number>;
  selectedRules: Map<number, RuleWithPath>;
  onToggleRule: (rule: RuleWithPath) => void;
};

export const TreeBlockNode: FC<TreeBlockNodeProps> = ({
  block,
  reportId,
  reportName,
  depth,
  blockPath,
  search,
  linkedSet,
  selectedRules,
  onToggleRule,
}) => {
  const [expandedBlocks, setExpandedBlocks] = useState<string[]>([String(block.id)]);

  const currentPath = useMemo(() => [...blockPath, block.name], [blockPath, block.name]);

  const visibleRules = useMemo(
    () => block.rules.filter((r) => !r.isDeleted && ruleMatchesSearch(r, search)),
    [block.rules, search],
  );

  const visibleChildren = useMemo(() => block.children.filter((c) => !c.isDeleted), [block.children]);

  const hasContent = useMemo(() => {
    if (visibleRules.length > 0) return true;
    return blocksHaveMatchingRules(visibleChildren, search);
  }, [visibleRules.length, visibleChildren, search]);

  const handleValueChange = useCallback((value: string[]) => {
    setExpandedBlocks(value);
  }, []);

  if (search && !hasContent) return null;

  const isExpandable = visibleRules.length > 0 || visibleChildren.length > 0;
  const paddingLeft = (depth + 1) * 16;

  if (!isExpandable) {
    return (
      <div className="flex items-center gap-2 rounded-md py-1.5 pr-2" style={{ paddingLeft: `${paddingLeft}px` }}>
        <div className="size-3.5 shrink-0" />
        <PackageIcon size={14} className="shrink-0 text-muted-foreground" />
        <span className="text-xs font-medium text-foreground">{block.name}</span>
      </div>
    );
  }

  return (
    <Accordion type="multiple" value={expandedBlocks} onValueChange={handleValueChange}>
      <AccordionItem value={String(block.id)} className="border-0">
        <AccordionTrigger
          iconSide="left"
          className="py-1.5 pr-2 hover:bg-muted/50 rounded-md"
          style={{ paddingLeft: `${paddingLeft}px` }}
        >
          <div className="flex items-center gap-2">
            <PackageIcon size={14} className="shrink-0 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">{block.name}</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-0 pb-0">
          {visibleRules.map((rule) => (
            <RuleCheckboxItem
              key={rule.id}
              rule={rule}
              block={block}
              reportId={reportId}
              reportName={reportName}
              blockPath={currentPath}
              isLinked={linkedSet.has(rule.id)}
              isSelected={selectedRules.has(rule.id)}
              onToggle={onToggleRule}
              depth={depth}
            />
          ))}
          {visibleChildren.map((child) => (
            <TreeBlockNode
              key={child.id}
              block={child}
              reportId={reportId}
              reportName={reportName}
              depth={depth + 1}
              blockPath={currentPath}
              search={search}
              linkedSet={linkedSet}
              selectedRules={selectedRules}
              onToggleRule={onToggleRule}
            />
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
